import type { ApolloCache } from "@apollo/client";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// Generic Apollo cache helpers for paginated connections.
//
// Works with any field that uses `offsetPagination` (users, teams, issues,
// etc.). Uses `cache.modify` which bypasses field policy merge functions.
//
// Entity identification uses `toReference` which respects the `keyFields`
// configured in type policies (e.g. User → userId, Team → teamId).
// ---------------------------------------------------------------------------

/**
 * Identifies an entity for cache operations.
 * Must include `__typename` and the keyField(s) for that type.
 *
 * @example
 * { __typename: "User", userId: "abc123" }
 * { __typename: "Team", teamId: "team456" }
 */
type EntityIdentifier = { __typename: string; [keyField: string]: string };

type StoreFieldFilter = Omittable<{
  /**
   * Only modify cache entries whose `storeFieldName` includes ALL of these
   * substrings. Use full key-value pairs to avoid collisions:
   *   `['"eq":"stageId123"']` — not just `["stageId123"]`
   *
   * If omitted, only first-page entries are modified (see `isFirstPage`) —
   * but EVERY first-page entry of the field, including each filter-scoped
   * variant (`where` is part of the store field name). That is right for an
   * unfiltered catalog field (issueSources, reasons, webhooks, …). A field
   * cached under filter variants the entity may not match (per-workflow
   * stages, filtered users lists) must pass `storeFieldNameIncludes` scoping
   * the write to entries whose filter the entity satisfies — an unscoped
   * insert lands the entity in lists it does not belong to.
   */
  storeFieldNameIncludes?: string[];
}>;

/**
 * A cache entry is a first page when its arguments carry `skip: 0` — or no
 * `skip` at all. Fields under `offsetPagination` key `skip` into the store
 * field name, so each page is a separate entry; fields queried without `skip`
 * (issueSources, reasons, webhooks, …) produce one entry with no `skip` in
 * the key, and that single entry IS the first page. Requiring the literal
 * `"skip":0` would silently skip those — the modifier would no-op and the
 * mutation's cache update would never land.
 */
function isFirstPage(storeFieldName: string): boolean {
  const match = /"skip":(\d+)/.exec(storeFieldName);
  return !match || match[1] === "0";
}

function matchesStoreField(
  storeFieldName: string,
  storeFieldNameIncludes: string[] | undefined,
): boolean {
  if (!storeFieldNameIncludes) return isFirstPage(storeFieldName);
  return storeFieldNameIncludes.every((substring) =>
    storeFieldName.includes(substring),
  );
}

interface InsertOptions extends StoreFieldFilter {
  /** "start" prepends, "end" appends. Default: "end" */
  position?: "start" | "end";
}

interface RemoveOptions extends StoreFieldFilter {
  /**
   * If true, also evicts the entity from the normalized cache and runs gc.
   * Use for hard deletes. Skip for soft removals (e.g., moving between views).
   */
  evict?: boolean;
}

/**
 * Insert an entity into a relay-style connection in the Apollo cache.
 *
 * - Deduplicates by `__ref` (safe for optimistic double-fire)
 * - Increments `total`
 * - Supports `storeFieldNameIncludes` to target specific argument combinations
 *
 * @example
 * update(cache, { data }) {
 *   if (data?.inviteUser?.__typename === "User") {
 *     insertIntoConnection(cache, "users", "UserEdge", {
 *       __typename: "User",
 *       userId: data.inviteUser.userId,
 *     });
 *   }
 * }
 */
export function insertIntoConnection(
  cache: ApolloCache,
  fieldName: string,
  edgeTypename: string,
  entity: EntityIdentifier,
  options: InsertOptions = {},
): void {
  // Default to first-page entries only, so we don't duplicate into every
  // per-page cache entry created by offsetPagination.
  const { storeFieldNameIncludes, position = "end" } = options;

  cache.modify({
    fields: {
      [fieldName](existing: any, { toReference, storeFieldName }: any) {
        if (!matchesStoreField(storeFieldName, storeFieldNameIncludes)) {
          return existing;
        }

        const ref = toReference(entity);
        if (!ref) return existing;

        const edges = existing?.edges ?? [];
        const alreadyExists = edges.some(
          (edge: any) => edge.node?.__ref === (ref as any).__ref,
        );
        if (alreadyExists) return existing;

        const newEdge = {
          __typename: edgeTypename,
          node: ref,
        };

        return {
          ...existing,
          edges:
            position === "start"
              ? [newEdge, ...edges]
              : [...edges, newEdge],
          total: (existing?.total ?? 0) + 1,
        };
      },
    },
  });
}

/**
 * Remove an entity from an offset-paginated connection in the Apollo cache.
 *
 * - Decrements `total` (clamped to 0)
 * - Optionally evicts the entity from the normalized cache
 *
 * @example
 * update(cache) {
 *   removeFromConnection(cache, "teams", {
 *     __typename: "Team", teamId: "team123",
 *   }, { evict: true });
 * }
 */
export function removeFromConnection(
  cache: ApolloCache,
  fieldName: string,
  entity: EntityIdentifier,
  options: RemoveOptions = {},
): void {
  // Default to first-page entries only, so we don't remove from every
  // per-page cache entry created by offsetPagination.
  const { storeFieldNameIncludes, evict = false } = options;

  cache.modify({
    fields: {
      [fieldName](existing: any, { toReference, storeFieldName }: any) {
        if (!matchesStoreField(storeFieldName, storeFieldNameIncludes)) {
          return existing;
        }

        const ref = toReference(entity);
        if (!ref) return existing;
        const refKey = (ref as any).__ref;

        const edges = existing?.edges ?? [];
        const filtered = edges.filter(
          (edge: any) => edge.node?.__ref !== refKey,
        );

        if (filtered.length === edges.length) return existing;

        return {
          ...existing,
          edges: filtered,
          total: Math.max(0, (existing?.total ?? 0) - 1),
        };
      },
    },
  });

  if (evict) {
    evictEntity(cache, entity);
  }
}

/**
 * Evict an entity from the normalized cache and garbage collect.
 *
 * `offsetPagination` automatically filters dangling references,
 * so eviction alone can clean up connections without manual edge removal.
 */
export function evictEntity(
  cache: ApolloCache,
  entity: EntityIdentifier,
): void {
  const cacheId = cache.identify(entity);
  // `identify` answers `undefined` for an object it cannot key, and the cache
  // reads an absent `id` as the ROOT — the eviction would drop a ROOT_QUERY
  // field instead of this entity.
  if (cacheId) cache.evict({ id: cacheId });
  cache.gc();
}
